import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import ConfirmationModal from "components/common/ConfirmationMessage";
import { ListLayout } from "components/common/ListLayout";
import { CustomSwitch } from "components/common/Switch";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import AddClientSubUsers from "components/subUsers/AddClientSubUsers";
import {
  adminClientSubUserSelector,
  fetchAdminClientSubUsers,
  changeAdminClientSubUserStatus,
  removeMultipleAdminClientSubUsers,
} from "slices/adminClientSubUserSlice";

function ClientSubUsersList() {
  const dispatch = useDispatch();
  const { id: clientId } = useParams();
  const { subUser: subUsers, last_page: totalPages, loader, total, current_page, per_page: pageLength } = useSelector(adminClientSubUserSelector);
  const [addForm, setAddForm] = React.useState({ show: false, item: {} });
  const refAddutton = React.useRef(null);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [sortConfig, setSortConfig] = React.useState({
    sortBy: 'created_at',
    sortDirection: 'desc',
  });

  // fetchList is dispatched by ListLayout as fetchList(page, params, disableLoader);
  // bind the clientId so pagination/search hit the right client's sub users.
  const fetchList = React.useCallback(
    (page, params, disableLoader) => fetchAdminClientSubUsers(clientId, page, params, disableLoader),
    [clientId]
  );

  React.useEffect(() => {
    dispatch(fetchAdminClientSubUsers(clientId));
  }, [clientId]);

  const handleAddTypePopUp = (item) => {
    setAddForm({ show: true, item });
  };

  const hideConfirmDialog = () => setDeleteModal(false);

  const submitCallback = (status, message) => {
    if (status) {
      toast.success(message, { className: "successToast" });
      setSelectedItems([]);
      setSelectAll(false);
      dispatch(fetchAdminClientSubUsers(clientId));
      hideConfirmDialog();
    } else {
      toast.error(message, { className: "errorToast" });
    }
  };

  const handleRemoveItems = React.useCallback(() => {
    dispatch(removeMultipleAdminClientSubUsers(clientId, { ids: selectedItems }, submitCallback));
  }, [selectedItems, clientId]);

  const handleChange = (e) => {
    const id = e.target.dataset.value;
    setSelectedItems(
      selectedItems.includes(id)
        ? selectedItems.filter((item) => item != id)
        : [...selectedItems, id]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
      return;
    }
    setSelectAll(true);
    setSelectedItems(subUsers.map((subUser) => subUser.id.toString()));
  };

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(fetchAdminClientSubUsers(clientId, currentPage, { sortBy, sortDirection: dir, search: searchTerm }));
    };

    return (
      <tr>
        <th className="col-1">
          <div className="row_check">
            <span>#</span>
            <label className="custom-checkbox checkbox"><input type="checkbox" name="select-all" checked={selectAll} onChange={handleToggleSelectAll} /><span className="checkmark"></span></label>
          </div>
        </th>
        <th><TableHeaderItem title="Name" sortBy="name" sortDirection={sortConfig.sortDirection} callBack={callBack} /></th>
        <th><TableHeaderItem title="Email" sortBy="email" sortDirection={sortConfig.sortDirection} callBack={callBack} /></th>
        <th><TableHeaderItem title="Gender" sortBy="gender" sortDirection={sortConfig.sortDirection} callBack={callBack} /></th>
        <th><TableHeaderItem title="Phone" sortBy="phone" sortDirection={sortConfig.sortDirection} callBack={callBack} /></th>
        <th>Status</th>
        <th>Actions</th>
      </tr>
    );
  }, [selectAll, searchTerm, sortConfig, currentPage, selectedItems, subUsers, clientId]);

  const renderTableContent = React.useCallback(() => {
    return (
      subUsers?.map((subUser, index) => (
        <SubUserItem key={subUser.id} subUser={subUser} index={index} selectedItems={selectedItems} handleChange={handleChange} handleAddPopUp={handleAddTypePopUp} current_page={current_page} pageLength={pageLength} clientId={clientId} />
      ))
    );
  }, [subUsers, selectedItems, current_page, clientId]);

  const handleClose = React.useCallback(() => {
    setAddForm({ show: false });
  }, []);

  const showConfirmDialog = () => setDeleteModal(true);

  return (
    <ListLayout
      heading="Sub Users"
      time={true}
      title=""
      buttonTitle="Add Sub User"
      renderTableHeader={renderTableHeader}
      renderTableContent={renderTableContent}
      totalPages={totalPages}
      fetchList={fetchList}
      selectedItems={selectedItems}
      handleRemoveItems={handleRemoveItems}
      renderAddForm={(callback) => <AddClientSubUsers item={addForm.item} show={addForm.show} onClose={handleClose} callback={callback} adminClientId={clientId} />}
      refAddutton={refAddutton}
      addForm={addForm}
      showConfirmDialog={showConfirmDialog}
      handleAddPopUp={handleAddTypePopUp}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      sortConfig={sortConfig}
      loader={loader}
      searchPlaceholder="Search sub users..."
      noOfItems={total}
      disableBootstrap
    >
      {deleteModal && <ConfirmationModal onClose={hideConfirmDialog} isOpen={deleteModal} onConfirm={handleRemoveItems} />}
    </ListLayout>
  );
}

const SubUserItem = ({ subUser, index, selectedItems, handleChange, handleAddPopUp, current_page, pageLength, clientId }) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState(subUser?.status == "active" ? 1 : 0);

  const handleStatusChange = (id, s) => {
    setStatus(s ? 1 : 0);
    dispatch(changeAdminClientSubUserStatus(clientId, id, { status: s ? 1 : 0 }));
  };

  React.useEffect(() => {
    setStatus(subUser?.status ?? 0);
  }, [subUser]);

  const getBackgroundStyle = (status) => (status === 0 ? { background: '#f4f4f4' } : {});
  const serial = useMemo(() => index + 1 + (current_page - 1) * pageLength, [current_page, pageLength]);

  return (
    <tr>
      <td style={getBackgroundStyle(status)}>
        <div className="row_check">
          <span>{serial}</span>
          <label className="custom-checkbox checkbox">
            <input type="checkbox" name="select-all" data-value={subUser.id} checked={selectedItems.includes(subUser.id.toString())} onChange={handleChange} />
            <span className="checkmark"></span>
          </label>
        </div>
      </td>
      <td style={getBackgroundStyle(status)}>{subUser?.user?.name || 'N/A'}</td>
      <td style={getBackgroundStyle(status)}>{subUser?.user?.email || 'N/A'}</td>
      <td style={getBackgroundStyle(status)}>{subUser?.gender || 'N/A'}</td>
      <td style={getBackgroundStyle(status)}>{subUser?.phone || 'N/A'}</td>
      <td style={getBackgroundStyle(status)}>
        <CustomSwitch handleChange={(status) => handleStatusChange(subUser?.id, status)} checked={status == 1 ? true : false} />
      </td>
      <td style={getBackgroundStyle(status)}>
        <div className="row_check">
          <button className="table_edit" onClick={() => handleAddPopUp({ ...subUser, status })}>Edit</button>
        </div>
      </td>
    </tr>
  );
};

export default ClientSubUsersList;
